"use client";

import { useState } from "react";
import { format } from "date-fns";
import Image from "next/image";
import Link from "next/link";
import { type BlogPost } from "../data/blog/types";
import { defaultLabels } from "@/(global)/olddashboard/blog/config/defaultLabels";
import { slugify } from "@/utils/slugify";
import { COChevroDown } from "@/assets/icons/co-chevro-down";

interface BlogGridProps {
  initialPosts: BlogPost[];
}

interface CategoryFilterProps {
  categories: Array<{ value: string; label: string }>;
  years: string[];
  selectedFilter: string;
  onSelectFilter: (filter: string) => void;
  posts: BlogPost[];
}

const CategoryFilter = ({
  categories,
  years,
  selectedFilter,
  onSelectFilter,
  posts,
}: CategoryFilterProps) => {
  const hasPostsForFilter = (filter: string) => {
    if (years.includes(filter)) {
      return posts.some(
        (post) => new Date(post.createdAt).getFullYear().toString() === filter,
      );
    }
    return posts.some((post) => post.labels.includes(filter));
  };

  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
      <div className="relative w-full md:w-auto">
        <div className="relative">
          <select
            value={selectedFilter}
            onChange={(e) => onSelectFilter(e.target.value)}
            className="w-full cursor-pointer appearance-none rounded-md border border-primary bg-white px-4 py-2.5 text-center text-sm tracking-wide transition-colors hover:bg-gray-50 md:w-auto md:rounded-none md:px-6"
            aria-label="Sélectionner une année"
          >
            <option value="ALL">Toutes les années</option>
            {years.map((year) => (
              <option
                key={year}
                value={year}
                disabled={!hasPostsForFilter(year)}
              >
                {year}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 transform">
            <COChevroDown className="h-4 w-4 text-gray-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap">
        {categories.map((category) => {
          const hasPosts = hasPostsForFilter(category.value);
          return (
            <button
              key={category.value}
              type="button"
              disabled={!hasPosts}
              className={`cursor-pointer rounded-md border border-primary px-3 py-2.5 text-center text-sm tracking-wide transition-colors md:rounded-none md:px-4 ${
                selectedFilter === category.value
                  ? "bg-primary text-white"
                  : hasPosts
                    ? "bg-white text-gray-900 hover:bg-gray-50"
                    : "cursor-not-allowed bg-white text-gray-400 opacity-50"
              }`}
              onClick={() => {
                if (hasPosts) {
                  onSelectFilter(
                    selectedFilter === category.value ? "ALL" : category.value,
                  );
                }
              }}
            >
              {category.label}
            </button>
          );
        })}
        {selectedFilter !== "ALL" && (
          <button
            type="button"
            className="cursor-pointer rounded-md border border-primary bg-white px-3 py-2.5 text-center text-sm tracking-wide text-gray-900 transition-colors hover:bg-gray-50 md:rounded-none md:px-4"
            onClick={() => onSelectFilter("ALL")}
          >
            Effacer les filtres
          </button>
        )}
      </div>
    </div>
  );
};

const BlogCard = ({ post }: { post: BlogPost }) => {
  const slug = `${slugify(post.titre)}-${slugify(post.sousTitre)}`;

  return (
    <Link href={`/blog/${slug}`} className="group flex flex-col">
      <div className="relative aspect-video w-full overflow-hidden">
        <Image
          src={post.images[0]}
          alt={post.titre}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          className="object-cover object-center transition-transform duration-300 group-hover:grayscale"
        />
      </div>
      <div className="mt-4">
        <h2 className="text-lg font-medium group-hover:text-gold">
          {post.titre}
        </h2>
        <p className="mt-2 line-clamp-2 text-sm text-gray-600">
          {post.sousTitre}
        </p>
        <time className="mt-2 block text-sm text-gray-500">
          {post.createdAt
            ? format(new Date(post.createdAt), "dd/MM/yyyy")
            : "Date non disponible"}
        </time>
        <div className="mt-2 flex flex-wrap gap-2">
          {post.labels.map((label, index) => (
            <span key={index} className="text-xs text-gray-500">
              #{label}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};

export const BlogGrid = ({ initialPosts }: BlogGridProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string>("ALL");
  const posts = initialPosts.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const years = Array.from(
    new Set(
      posts.map((post) => new Date(post.createdAt).getFullYear().toString()),
    ),
  ).sort((a, b) => parseInt(b) - parseInt(a));

  const filteredPosts = posts.filter((post) => {
    if (selectedFilter === "ALL") return true;
    if (years.includes(selectedFilter)) {
      return (
        new Date(post.createdAt).getFullYear().toString() === selectedFilter
      );
    }
    return post.labels.includes(selectedFilter);
  });

  return (
    <section className="w-full px-4 pb-20 pt-8 ph:px-24 md:px-48 lg:px-72 xl:px-96">
      <CategoryFilter
        categories={defaultLabels}
        years={years}
        selectedFilter={selectedFilter}
        onSelectFilter={setSelectedFilter}
        posts={posts}
      />

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {filteredPosts.map((post) => (
          <BlogCard key={post.id} post={post} />
        ))}
      </div>

      <div className="mt-16 text-center text-sm text-gray-500">
        <p className="mx-auto max-w-2xl leading-relaxed">
          Découvrez le blog d&apos;Alexandra Grey et son salon concept &quot;Au
          grey des couleurs&quot;. Situé en Val de Marne (94), ce salon de
          coiffure unique accueille une clientèle de Villejuif, Ville
          d&apos;Avray et des environs. Retrouvez nos conseils professionnels,
          les dernières tendances capillaires et l&apos;actualité de ce salon
          concept innovant.
        </p>
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="mx-auto mt-12 block rounded-md border border-primary bg-white px-6 py-3 text-center text-sm tracking-wide text-gray-900 transition-colors hover:bg-gray-50"
      >
        Retourner en haut avec Alexandra Grey
      </button>
    </section>
  );
};
